import React, { useEffect, useRef, useState } from "react";
import { Input } from "../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../shadcn/components/ui/button";
import { BetaForm as useBetaForm } from "@reusejs/react";
import {
  fetchIDCardDetails,
  getBidderByUuid,
  updateBidderForEvent,
} from "services/bidder";
import CustomMultipleFileUploader from "../../../../banker/bidder/CustomMultipleFileUploader";

const isDigioPanFullVerificationEnabled =
  process.env.NEXT_PUBLIC_DIGIO_PAN_FULL_VERIFICATION_ENABLED === "true";

interface AssignRepresentativeComponentProps {
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  bidderUuid: any;
  init: any;
}
export default function AssignRepresentativeComponent({
  bidderUuid,
  init,
  stepsTitles,
  activeStep,
  setActiveStep,
}: AssignRepresentativeComponentProps) {
  const [loading, setLoading] = useState(false);
  const [isFetchingPan, setIsFetchingPan] = useState(false);
  const [isNameVerified, setIsNameVerified] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);

  // V4 name-first flow: debounce ref for PAN verification trigger
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // const [stage, setStage] = useState<any>(null);
  // const form = useBetaForm({
  //   mobile: "",
  //   email: "",
  //   pan: "",
  //   name: "",
  //   designation: "",
  //   ar_authorization_letter: "",
  // });
  //TODO:remove in staging to enable the document upload functionality
  const form = useBetaForm({
    mobile: "",
    email: "",
    pan: "",
    name: "",
    designation: "",
    ar_authorization_letter: "",
  });
  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
  const emailRegEx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const panRegex: RegExp = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const form_constraints = {
    mobile: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    email: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: emailRegEx,
        message: "^Email is not valid.",
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
      },
      format: {
        pattern: panRegex,
      },
    },
    name: {
      presence: {
        allowEmpty: true,
      },
    },
    designation: {
      presence: {
        allowEmpty: false,
      },
    },
    ar_authorization_letter: {
      presence: {
        allowEmpty: false,
      },
    },
  };

  // V4 name-first flow: Debounced function to trigger PAN verification when name >= 3 chars
  const triggerPanVerification = () => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      const currentPan = form.getField("pan") || "";
      const currentName = (form.getField("name") || "").toString().trim();

      // Only trigger if name >= 3 AND PAN has value
      if (
        currentName.length >= 3 &&
        currentPan.length === 10 &&
        panRegex.test(currentPan)
      ) {
        handlePanVerification(
          currentPan,
          form,
          setIsNameVerified,
          setIsFetchingPan,
          setLoadingText,
        );
      }
    }, 500);
  };

  // Internal PAN verification function - called from debounced trigger or direct onChange
  const handlePanVerification = async (
    panValue: string,
    formInstance: any,
    setIsNameVerifiedFn: (value: boolean) => void,
    setIsFetchingPanFn: (value: boolean) => void,
    setLoadingTextFn: (text: string) => void,
  ) => {
    try {
      setIsFetchingPanFn(true);
      setLoadingTextFn("Fetching PAN details. Please wait...");

      const payload: any = {
        card_type: "PAN",
        card_number: panValue,
      };

      // Add name if full verification is enabled AND name has at least 3 chars
      const userName = (formInstance.getField("name") || "").toString().trim();
      if (isDigioPanFullVerificationEnabled && userName.length >= 3) {
        payload.name = userName.toUpperCase();
      }

      const res: any = await fetchIDCardDetails(payload);
      const panCategory = (res?.category || "").toString().toLowerCase();
      const panStatus = (res?.status || "").toString().toLowerCase();
      const nameAsPerPanMatch = res?.name_as_per_pan_match;
      const userEnteredName = (formInstance.getField("name") || "")
        .toString()
        .trim();

      // Check if PAN status is valid first (independent of name)
      const isPanStatusValid = panStatus === "valid";

      // Only check name_as_per_pan_match if name has at least 3 characters
      const isNameLongEnough = userEnteredName.length >= 3;
      const isNameMatch = isDigioPanFullVerificationEnabled
        ? isNameLongEnough && nameAsPerPanMatch === true
        : true;

      if (panCategory !== "individual") {
        formInstance.pushError({
          pan: ["Representative PAN must be an individual PAN."],
        });
        if (!isDigioPanFullVerificationEnabled) {
          formInstance.setField("name", "");
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      if (!isPanStatusValid) {
        formInstance.pushError({
          pan: ["PAN seems invalid. Please check the number and try again."],
        });
        if (!isDigioPanFullVerificationEnabled) {
          formInstance.setField("name", "");
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      if (!isNameMatch) {
        if (!isNameLongEnough) {
          formInstance.pushError({
            pan: [
              "Please enter your name (minimum 3 characters) before PAN verification.",
            ],
          });
        } else {
          formInstance.pushError({
            pan: [
              "The name doesn't match your PAN records. Please enter your name exactly as on your PAN card.",
            ],
          });
        }
        setIsNameVerifiedFn(false);
        setIsFetchingPanFn(false);
        setLoadingTextFn("");
        return;
      }

      // Success - clear errors
      formInstance.pushError({ pan: [] });

      // V4 mode: Don't overwrite user's entered name
      // Legacy mode: Use full_name from API response
      if (!isDigioPanFullVerificationEnabled) {
        formInstance.setField("name", res?.full_name);
      }
      setIsNameVerifiedFn(true);
      setIsFetchingPanFn(false);
      setLoadingTextFn("");
    } catch (error: any) {
      console.log("PAN verification error:", error);

      if (error?.response?.status === 422) {
        const errorMessage =
          error?.response?.data?.message?.message ||
          "Validation failed. Please check your details.";
        formInstance.pushError({
          pan: [errorMessage],
        });
      } else {
        formInstance.pushError({
          pan: ["An internal error occurred. Please try again later."],
        });
      }

      if (!isDigioPanFullVerificationEnabled) {
        formInstance.setField("name", "");
      }
      setIsNameVerifiedFn(false);
      setIsFetchingPanFn(false);
      setLoadingTextFn("");
    }
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form.value]);
  //TODO:remove in staging to enable the document upload functionality
  // useEffect(() => {
  //   (async () => {
  //     setLoading(true);
  //     setLoadingText("Loading Assigned representative Data.Please wait...");
  //     const data: any = await getBidderByUuid(bidderUuid);
  //     setStage(data?.application_form_data?.ar_step || null);
  //     if (data?.application_form_data?.ar_step === "upload_ar_kyc_doc") {
  //       form.setField(
  //         "name",
  //         data?.application_form_data?.ar_details?.pan_name || ""
  //       );
  //     }
  //     setLoading(false);
  //     setLoadingText("");
  //   })();
  // }, [bidderUuid]);

  const handleSubmit = async () => {
    setLoading(true);
    console.log("form", form);
    if (form.validate()) {
      // const userData: any = await getUserByUserMobile({
      //   mobile: form.getField("mobile"),
      // });
      // const userData: any = await postDataWithHeaders();
      // if (userData?.message) {
      //   form.pushError({
      //     mobile: [
      //       "Mobile number not registered with us. Please register and try again.",
      //     ],
      //   });
      //   setLoading(false);
      //   return;
      // }

      const data: any = await getBidderByUuid(bidderUuid);
      const res5: any = await updateBidderForEvent({
        uuid: bidderUuid,
        application_form_data: {
          ...data?.application_form_data,
          ar_details: {
            user_mobile: form.getField("mobile"),
            email: form.getField("email"),
            pan: form.getField("pan"),
            pan_name: form.getField("name"),
            designation: form.getField("designation"),
            ar_authorization_letter: kycFiles[0],
            status: "completed",
            reject_remarks: null,
            verification_step: null,
          },
          //TODO:remove in staging to enable the document upload functionality
          ar_step: "upload_ar_kyc_doc",
        },
        //TODO:remove in staging to enable the document upload functionality
        verification_step: "add_ar",
      });
      setLoadingText(
        "Assigned representative. Redirecting to the next step. Please wait...",
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setActiveStep(activeStep + 1);
      await init();
      //TODO:remove in staging to enable the document upload functionality
      // setStage("upload_ar_kyc_doc");
      setLoading(false);
    }
    setLoading(false);
  };
  return (
    <div>
      {/*//TODO:remove in staging to enable the document upload functionality {stage !== "upload_ar_kyc_doc" ? ( */}
      <div>
        <h4 className="font-bold text-lg font-openSans">
          {stepsTitles?.[activeStep]?.label}
        </h4>
        <div className="mt-4 min-h-[300px]">
          <div className="text-xs md:text-sm font-openSans text-brand-textGrey">
            Please enter the details of the authorized representative below:
          </div>
          <div className="mt-4">
            {/* V4 mode: Email field comes FIRST - before name and PAN */}
            {isDigioPanFullVerificationEnabled && (
              <div className="mt-4">
                <Input
                  placeholder="Email*"
                  value={form.getField("email")}
                  onChange={(e: any) => {
                    const input = e.target;
                    const start = input?.selectionStart;
                    const end = input?.selectionEnd;
                    const lower = input.value?.toLowerCase();
                    form.setField("email", lower);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(start, end);
                    });
                  }}
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                />
                {form.errors.get("email") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("email")?.[0]}
                  </p>
                ) : null}
              </div>
            )}

            {/* V4 mode: Name field - AFTER email, BEFORE PAN */}
            {isDigioPanFullVerificationEnabled && (
              <div className="mt-4">
                <div className="relative">
                  {isFetchingPan && (
                    <div className="absolute top-1/3 right-2 z-10">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-orange"></div>
                    </div>
                  )}
                  {isNameVerified && !isFetchingPan && (
                    <div className="absolute top-1/3 right-2 z-10">
                      <svg
                        className="h-5 w-5 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                  <Input
                    disabled={loading}
                    placeholder={"Name (as per PAN)*"}
                    value={form.getField("name")}
                    onChange={(e: any) => {
                      const value = e?.target?.value?.toUpperCase() || "";
                      form.setField("name", value);
                      form.pushError({ name: [] }); // Clear name errors when typing
                      // V4 name-first flow: trigger PAN verification after name >= 3 chars
                      triggerPanVerification();
                    }}
                    className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                  />
                </div>
                {form.errors.get("name") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("name")?.[0]}
                  </p>
                ) : null}
              </div>
            )}

            {/* PAN field - FIRST in legacy mode, AFTER name in V4 mode */}
            <div className="mt-4">
              <div className="relative">
                {isFetchingPan && (
                  <div className="absolute top-1/3 right-2 z-10">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-orange"></div>
                  </div>
                )}
                <Input
                  disabled={
                    loading ||
                    (isDigioPanFullVerificationEnabled &&
                      (form.getField("name") || "").length < 3)
                  }
                  placeholder={"Representative PAN Number*"}
                  value={form.getField("pan")}
                  onChange={(e: any) => {
                    if (e?.target?.value?.length < 11) {
                      let value = e?.target?.value.toUpperCase();
                      form.setField("pan", value);
                      form.pushError({ pan: [] }); // Clear errors when PAN changes

                      // V4 mode: trigger verification when PAN is complete AND name >= 3 chars
                      if (isDigioPanFullVerificationEnabled) {
                        const currentName = (form.getField("name") || "")
                          .toString()
                          .trim();
                        if (
                          currentName.length >= 3 &&
                          value.length === 10 &&
                          panRegex.test(value)
                        ) {
                          triggerPanVerification();
                        } else if (value.length > 0 && value.length < 10) {
                          // Clear verification state if PAN is incomplete
                          setIsNameVerified(false);
                        }
                      }

                      // Legacy mode: verify immediately on valid PAN
                      if (
                        !isDigioPanFullVerificationEnabled &&
                        value.length === 10 &&
                        panRegex.test(value)
                      ) {
                        handlePanVerification(
                          value,
                          form,
                          setIsNameVerified,
                          setIsFetchingPan,
                          setLoadingText,
                        );
                      } else if (!isDigioPanFullVerificationEnabled) {
                        // Legacy mode: clear name when PAN changes
                        form.setField("name", "");
                        setIsNameVerified(false);
                      }
                    }
                  }}
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                />
              </div>
              {/* Show name verification status in V4 mode */}
              {isDigioPanFullVerificationEnabled &&
                isNameVerified &&
                !isFetchingPan && (
                  <div className="text-green-600 capitalize text-xs ml-3 mt-1 flex items-center gap-1">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {form.getField("name")} - PAN Verified
                  </div>
                )}
              {form.errors.get("pan") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("pan")?.[0]}
                </p>
              ) : null}
            </div>

            {/* Mobile field */}
            <div className="mt-4">
              <Input
                type="number"
                placeholder={"Mobile*"}
                value={form.getField("mobile")}
                onChange={(e: any) => {
                  if (e.target.value >= 0 || e.target.value == "") {
                    form.setField("mobile", e?.target?.value);
                    form.pushError({
                      mobile: [],
                    });
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("mobile") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("mobile")?.[0]}
                </p>
              ) : null}
            </div>

            {/* Legacy mode: Email field comes AFTER mobile - PAN autofills name from API */}
            {!isDigioPanFullVerificationEnabled && (
              <div className="mt-4">
                <Input
                  placeholder="Email*"
                  value={form.getField("email")}
                  onChange={(e: any) => {
                    const input = e.target;
                    const start = input?.selectionStart;
                    const end = input?.selectionEnd;
                    const lower = input.value?.toLowerCase();
                    form.setField("email", lower);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(start, end);
                    });
                  }}
                  className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
                />
                {form.errors.get("email") ? (
                  <p className="mt-1 text-sm text-red-500">
                    {form.errors.get("email")?.[0]}
                  </p>
                ) : null}
              </div>
            )}

            {/* Designation field */}
            <div className="mt-4">
              <Input
                disabled={loading}
                placeholder={"Designation*"}
                value={form.getField("designation")}
                onChange={async (e: any) => {
                  form.setField("designation", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("designation") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("designation")[0]}
                </p>
              ) : null}
            </div>

            {/* File uploader */}
            <div className="mt-4">
              <CustomMultipleFileUploader
                key={renderKey}
                handleChange={(data: any) => {
                  console.log("id", data?.[0]?.id);
                  form.setField("ar_authorization_letter", data?.[0]?.id || "");
                  setKycFiles(data);
                }}
                allowedMimeTypes={["image/png", "image/jpeg"]}
                property_uuid={bidderUuid}
                owner_service_type="ar-authorization-letter"
                data={kycFiles}
                maxAllowed={1}
                defaultText="Please upload Authorization Letter / Board Resolution"
              />
              {form.errors.get("ar_authorization_letter") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("ar_authorization_letter")[0]}
                </p>
              ) : null}
            </div>

            {/* Submit button */}
            <div className="mt-8 flex justify-center">
              <Button
                disabled={loading}
                className="w-full max-w-xl h-10 text-[18px]"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
        {(loading || isFetchingPan) && (
          <LoadingModal loadingText={loadingText} />
        )}
      </div>
      {/* ) : (
        <div>
          <h4 className="font-bold text-lg font-openSans">
            {stepsTitles?.[activeStep]?.label} Documents
          </h4>
          <AuthorizedRepresentativeDocumentUploader
            loading={loading}
            setLoading={setLoading}
            bidderUuid={bidderUuid}
            arName={form.getField("name") || ""}
            setLoadingText={setLoadingText}
            init={init}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
          {loading && <LoadingModal loadingText={loadingText} />}
        </div>
      )} */}
    </div>
  );
}
interface LoadingModalProps {
  loadingText?: string;
}
const LoadingModal = ({ loadingText }: LoadingModalProps) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className=" text-white flex flex-col relative">
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
          <div className="mt-2 text-sm font-openSans text-center">
            {loadingText || "Validating Details. Please wait..."}
          </div>
        </div>
      </div>
    </div>
  );
};
//TODO:remove in staging to enable the document upload functionality
// interface AuthorizedRepresentativeDocumentUploaderProps {
//   bidderUuid: string;
//   loading?: boolean;
//   setLoading: any;
//   arName: string;
//   setLoadingText: any;
//   init: any;
//   setActiveStep: any;
//   activeStep: any;
//   // arPanFiles?: any[];
//   // arAadhaarFiles?: any[];
//   // arPanUploaderRef?: React.RefObject<any>;
//   // arAadhaarUploaderRef?: React.RefObject<any>;
// }
// const AuthorizedRepresentativeDocumentUploader = ({
//   bidderUuid,
//   loading,
//   setLoading,
//   arName,
//   setLoadingText,
//   init,
//   activeStep,
//   setActiveStep,
// }: AuthorizedRepresentativeDocumentUploaderProps) => {
//   const [renderKey, setRenderKey] = useState(0);
//   const [arPanFiles, setArPanFiles] = useState<any[]>([]);
//   const [arAadhaarFrontFiles, setArAadhaarFrontFiles] = useState<any[]>([]);
//   const [arPanOCRData, setArPanOCRData] = useState<any>({});
//   const [arAadhaarBackFiles, setArAadhaarBackFiles] = useState<any[]>([]);
//   const [arAddharOCRData, setArAadharOCRData] = useState<any>({});
//   const arAadhaarFrontUploaderRef = React.useRef<any>(null);
//   const arAadhaarBackUploaderRef = React.useRef<any>(null);
//   const arPanUploaderRef = React.useRef<any>(null);

//   const form = useBetaForm({
//     is_pan_verified: "",
//     is_aadhar_front_verified: "",
//     is_aadhar_back_verified: "",
//     is_both_side_aadhar_uploaded: false,
//   });
//   const form_constraints = {
//     is_pan_verified: {
//       presence: {
//         allowEmpty: false,
//         message: "^Pan must be verified before submitting.",
//       },
//       inclusion: {
//         within: [true],
//         message: "^Pan must be verified before submitting.",
//       },
//     },
//     is_aadhar_front_verified: {
//       presence: {
//         allowEmpty: false,
//         message: "^Aadhar Front must be verified before submitting.",
//       },
//       inclusion: {
//         within: [true],
//         message: "^Aadhar Front be verified before submitting.",
//       },
//     },
//     is_aadhar_back_verified: {
//       presence: {
//         allowEmpty: false,
//         message: "^Aadhar Back must be verified before submitting.",
//       },
//       inclusion: {
//         within: [true],
//         message: "^Aadhar Back must be verified before submitting.",
//       },
//     },
//   };
//   useEffect(() => {
//     form.setValidationRules(form_constraints);
//   }, [form.value]);

//   const handlePanDocumentChange = async (data: any) => {
//     console.log("id", data?.[0]?.id);

//     if (data?.[0]?.id) {
//       setLoading(true);
//       setLoadingText(
//         "Verifying Authorized Representative's PAN. Please wait..."
//       );
//       form.removeError("is_pan_verified");
//       form.setField("is_pan_verified", false);
//       let res: any = null;
//       let attempt = 0;
//       let success = false;
//       while (attempt < 3 && !success) {
//         try {
//           res = await fetchIDCardOCRDetails({
//             enable_both_sides: false,
//             check_id_match: false,
//             front_part: data?.[0]?.id,
//           });
//           console.log(`Attempt ${attempt + 1}:`, res);
//           if (res?.detections?.length) {
//             success = true;
//             form.removeError("is_pan_verified");
//           }
//         } catch (error: any) {
//           console.log(
//             `Error on attempt ${attempt + 1}:`,
//             error,
//             error?.response?.data?.status
//           );
//           if (error?.response?.status === 400) {
//             const message: any = error?.response?.data?.errorMessage;
//             form.setErrors({ is_pan_verified: [message] });
//             attempt = 4;
//           }
//           attempt++;
//           if (attempt < 3) {
//             await new Promise((resolve) => setTimeout(resolve, 18000));
//             if (attempt === 1) {
//               setLoadingText(
//                 "This is taking longer than expected. Please wait a moment..."
//               );
//             }
//           } else if (attempt === 3) {
//             const message: any = error?.response?.data?.errorMessage;
//             form.setErrors({ is_pan_verified: [message] });
//           }
//         }
//       }
//       if (!success) {
//         await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
//         form.setField("is_pan_verified", false);
//         setLoading(false);
//         setLoadingText("");
//         return;
//       } else {
//         if (res?.detections?.length > 0) {
//           const detection = res.detections[0];
//           if (detection.id_type !== "PAN") {
//             form.setErrors({
//               is_pan_verified: [
//                 "Please upload a valid PAN document of the Authorized Representative.",
//               ],
//             });
//           }
//           if (detection?.detected_part !== "FRONT") {
//             form.setErrors({
//               is_pan_verified: [
//                 "Upload a valid FRONT side of your PAN document to continue.",
//               ],
//             });
//           }
//           const fuzzyMatchPayload: any = {
//             context: "name",
//             target_text: arName,
//           };
//           fuzzyMatchPayload["source_text"] =
//             detection?.id_attributes?.name || "";

//           const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);
//           if (!res2?.matched) {
//             console.log("here----????1");
//             form.setErrors({
//               is_pan_verified: [
//                 `Name validation failed. Document name: ${fuzzyMatchPayload[
//                   "source_text"
//                 ]?.toLowerCase()}, AR name is: ${arName?.toLowerCase()}. Please contact support.`,
//               ],
//             });
//             setLoading(false);
//             await arPanUploaderRef.current?.deleteFile(data?.[0]?.id);
//           } else {
//             console.log("here----????2");
//             form.setField("is_pan_verified", true);
//             setArPanOCRData(res);
//           }
//         }
//       }
//       // Success logic here
//       form.setField("pan_uuid", data?.[0]?.id || "");
//       setArPanFiles(data);
//       setLoadingText("");
//       setLoading(false);
//     }
//   };
//   const handleAadhaarDocumentVerification = async (
//     arAadhaarFrontFiles: any,
//     arAadhaarBackFiles: any
//   ) => {
//     if (arAadhaarFrontFiles.length > 0 && arAadhaarBackFiles.length > 0) {
//       setLoadingText("");
//       setLoading(true);
//       setLoadingText(
//         "Verifying Aadhaar of Authorized Representative. Please wait..."
//       );
//       form.removeError("is_aadhar_front_verified");
//       form.removeError("is_aadhar_back_verified");
//       form.setField("is_both_side_aadhar_uploaded", false);
//       form.setField("is_aadhar_front_verified", false);
//       form.setField("is_aadhar_back_verified", false);
//       let res: any = null;
//       let attempt = 0;
//       let success = false;
//       while (attempt < 3 && !success) {
//         try {
//           res = await fetchIDCardOCRDetails({
//             enable_both_sides: true,
//             check_id_match: false,
//             front_part: arAadhaarFrontFiles?.[0]?.id,
//             back_part: arAadhaarBackFiles?.[0]?.id,
//           });
//           console.log(`Attempt ${attempt + 1}:`, res);
//           if (res?.detections?.length) {
//             success = true;
//             // form.removeError("is_aadhar_front_verified");
//             form.removeError("is_aadhar_back_verified");
//           }
//         } catch (error: any) {
//           console.log(`Error on attempt ${attempt + 1}:`, error);
//           if (error?.response?.status === 400) {
//             const message: any = error?.response?.data?.errorMessage;
//             form.setErrors({ is_aadhar_back_verified: [message] });
//             attempt = 4;
//           }
//           attempt++;
//           if (attempt < 3) {
//             await new Promise((resolve) => setTimeout(resolve, 18000));
//             setLoadingText(
//               "This is taking longer than expected. Please wait a moment..."
//             );
//           } else if (attempt === 3) {
//             const message: any = error?.response?.data?.errorMessage;
//             console.log("message: ", message, error?.response, error);
//             form.setErrors({ is_aadhar_back_verified: [message] });
//           }
//         }
//       }
//       if (!success) {
//         await arAadhaarFrontUploaderRef.current?.deleteFile(
//           arAadhaarFrontFiles?.[0]?.id
//         );
//         await arAadhaarBackUploaderRef.current?.deleteFile(
//           arAadhaarBackFiles?.[0]?.id
//         );
//         form.setField("is_both_side_aadhar_uploaded", false);
//         setLoadingText("");
//         setLoading(false);
//         return;
//       } else {
//         if (res?.detections?.length > 0) {
//           const detection = res.detections[0];
//           if (detection.id_type !== "AADHAAR") {
//             form.setErrors({
//               is_doc_verified: [
//                 "Please upload a valid PAN document for your user type.",
//               ],
//             });
//           }
//           if (detection?.detected_part !== "BOTH") {
//             form.setErrors({
//               is_doc_verified: [
//                 "Upload a valid FRONT side of your PAN document to continue.",
//               ],
//             });
//           }
//           const fuzzyMatchPayload: any = {
//             context: "name",
//             target_text: arName,
//           };
//           fuzzyMatchPayload["source_text"] =
//             detection?.id_attributes?.name || "";
//           const res2: any = await fetchFuzzyMatchDetails(fuzzyMatchPayload);
//           if (!res2?.matched) {
//             // setValidationErrorModelValue({
//             //   source_text: fuzzyMatchPayload["source_text"],
//             //   target_text: onboardingData?.name,
//             // });
//             // setValidationErrorModelOpen(true);
//             // setOpen(false);
//             // await uploaderRef.current?.deleteFile(data?.[0]?.id);

//             form.setErrors({
//               is_doc_verified: [
//                 `Name validation failed. Document name: ${fuzzyMatchPayload[
//                   "source_text"
//                 ]?.toLowerCase()}, AR name is: ${arName?.toLowerCase()}. Please contact support.`,
//               ],
//             });
//             setLoading(false);
//             return;
//           } else {
//             form.setField("is_aadhar_front_verified", true);
//             form.setField("is_aadhar_back_verified", true);
//             form.setField("is_both_side_aadhar_uploaded", true);
//             setArAadharOCRData(res);
//           }
//         }
//       }
//       setLoadingText("");
//       setLoading(false);
//     }
//   };
//   const handleSubmit = async () => {
//     setLoading(true);
//     if (form.validate()) {
//       setLoadingText(
//         "Saving the Authorized Representative details. Please wait..."
//       );
//       const data: any = await getBidderByUuid(bidderUuid);
//       const res5: any = await updateBidderForEvent({
//         uuid: bidderUuid,
//         application_form_data: {
//           ...data?.application_form_data,
//           ar_doc_details: {
//             pan: arPanFiles?.[0]?.id || "",
//             aadhaar_front: arAadhaarFrontFiles?.[0]?.id || "",
//             aadhaar_back: arAadhaarBackFiles?.[0]?.id || "",
//             // pan_ocr_data: arPanOCRData,
//             // aadhaar_ocr_data: arAddharOCRData,
//           },
//           ar_step: "ar_kyc_doc_uploaded",
//         },
//         verification_step: "bid_details",
//       });

//       await new Promise((resolve) => setTimeout(resolve, 1000));
//       setActiveStep(activeStep + 1);
//       await init();
//       setLoadingText("Redirecting to the next step. Please wait...");
//       setLoading(false);
//     }
//     setLoading(false);
//   };

//   // useEffect(() => {
//   //   (async () => {
//   //     if (arAadhaarFrontFiles.length > 0 && arAadhaarBackFiles.length > 0) {
//   //       console.log("Both Aadhaar files are uploaded");
//   //       await handleAadhaarDocumentVerification();
//   //     }
//   //   })();
//   // }, [form.getField("is_both_side_aadhar_uploaded")]);
//   return (
//     <div>
//       <div>
//         {/* AR PAN Upload */}
//         <div className="mt-4 text-sm font-semibold">PAN</div>
//         <div className="mt-4 max-w-xl">
//           {form.getField("is_pan_verified") === true ? (
//             <div className="border rounded p-4 bg-brand-bgLightGrey flex flex-col items-center">
//               <img
//                 src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arPanFiles[0].id}`}
//                 alt="Authorized Representative PAN"
//                 className="w-auto h-32 object-contain mb-2 border opacity-10"
//               />
//               <p className="mt-1 text-sm text-green-500 capitalize font-semibold">
//                 PAN is verified successfully for{" "}
//                 {arPanOCRData?.id_attributes?.name?.toLowerCase() ||
//                   "Authorized Representative"}
//                 .
//               </p>
//             </div>
//           ) : (
//             <CustomMultipleFileUploader
//               key={renderKey + "-ar-pan"}
//               handleChange={handlePanDocumentChange}
//               allowedMimeTypes={["image/png", "image/jpeg"]}
//               property_uuid={bidderUuid}
//               owner_service_type="ar_pan"
//               data={arPanFiles}
//               maxAllowed={1}
//               defaultText="Please upload Authorized Representative PAN*"
//               ref={arPanUploaderRef}
//             />
//           )}
//           {form.errors.get("is_pan_verified") ? (
//             <p className="mt-1 text-sm text-red-500">
//               {form.errors.get("is_pan_verified")[0]}
//             </p>
//           ) : null}
//         </div>
//         <div className="mt-4 text-sm font-semibold">Aadhaar Front and Back</div>
//         {/* AR Aadhaar Front Upload */}
//         <div className="mt-4 max-w-xl">
//           {form.getField("is_aadhar_front_verified") === true ? (
//             <div className="border rounded p-4 bg-brand-bgLightGrey flex flex-col items-center">
//               <img
//                 src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arAadhaarFrontFiles[0].id}`}
//                 alt="Authorized Representative PAN"
//                 className="w-auto h-32 object-contain mb-2 border opacity-10"
//               />
//               <p className="mt-1 text-sm text-green-500 capitalize font-semibold">
//                 Aadhaar Front is verified successfully for{" "}
//                 {arPanOCRData?.id_attributes?.name?.toLowerCase() ||
//                   "Authorized Representative"}
//                 .
//               </p>
//             </div>
//           ) : (
//             <CustomMultipleFileUploader
//               key={renderKey + "-ar-aadhaar-front"}
//               handleChange={async (data: any) => {
//                 setArAadhaarFrontFiles(data);
//                 if (data?.[0]?.id && arAadhaarBackFiles?.[0]?.id) {
//                   form.setField("is_both_side_aadhar_uploaded", true);
//                   console.log("Both Aadhaar files are uploaded");
//                   await handleAadhaarDocumentVerification(
//                     data,
//                     arAadhaarBackFiles
//                   );
//                 }
//               }}
//               allowedMimeTypes={["image/png", "image/jpeg"]}
//               property_uuid={bidderUuid}
//               owner_service_type="ar_aadhaar-front"
//               data={arAadhaarBackFiles}
//               maxAllowed={1}
//               defaultText="Please upload Authorized Representative Aadhaar Front*"
//               ref={arAadhaarFrontUploaderRef}
//             />
//           )}
//         </div>
//         {/* AR Aadhaar Back Upload */}
//         <div className="mt-4 max-w-xl">
//           {form.getField("is_aadhar_back_verified") === true ? (
//             <div className="border rounded p-4 bg-brand-bgLightGrey flex flex-col items-center">
//               <img
//                 src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${arAadhaarBackFiles[0].id}`}
//                 alt="Authorized Representative PAN"
//                 className="w-auto h-32 object-contain mb-2 border opacity-10"
//               />
//               <p className="mt-1 text-sm text-green-500 capitalize font-semibold">
//                 Aadhaar Back is verified successfully for{" "}
//                 {arPanOCRData?.id_attributes?.name?.toLowerCase() ||
//                   "Authorized Representative"}
//                 .
//               </p>
//             </div>
//           ) : (
//             <CustomMultipleFileUploader
//               key={renderKey + "-ar-aadhaar-back"}
//               handleChange={async (data: any) => {
//                 setArAadhaarBackFiles(data);
//                 if (data?.[0]?.id && arAadhaarFrontFiles?.[0]?.id) {
//                   form.setField("is_both_side_aadhar_uploaded", true);
//                   console.log("Both Aadhaar files are uploaded");
//                   await handleAadhaarDocumentVerification(
//                     arAadhaarFrontFiles,
//                     data
//                   );
//                 }
//               }}
//               allowedMimeTypes={["image/png", "image/jpeg"]}
//               property_uuid={bidderUuid}
//               owner_service_type="ar_aadhaar-back"
//               data={arAadhaarBackFiles}
//               maxAllowed={1}
//               defaultText="Please upload Authorized Representative Aadhaar Back*"
//               ref={arAadhaarBackUploaderRef}
//             />
//           )}

//           {form.errors.get("is_aadhar_back_verified") ? (
//             <p className="mt-1 text-sm text-red-500">
//               {form.errors.get("is_aadhar_back_verified")[0]}
//             </p>
//           ) : null}
//         </div>
//         {form.getField("is_pan_verified") === true &&
//           form.getField("is_aadhar_front_verified") === true &&
//           form.getField("is_aadhar_back_verified") === true && (
//             <div className="mt-8 flex">
//               <Button
//                 disabled={loading}
//                 className="w-full max-w-xl h-10 text-[18px]"
//                 onClick={handleSubmit}
//               >
//                 Next
//               </Button>
//             </div>
//           )}
//       </div>
//     </div>
//   );
// };
